"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="16906" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-accent/20 flex items-center justify-center mb-4">
          <Send className="size-8 text-accent" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-primary">Request Received!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
          Send another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. John Smith" {...field} className="bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-accent focus-visible:ring-accent/50 transition-all duration-300 hover:border-accent/30" />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-accent focus-visible:ring-accent/50 transition-all duration-300 hover:border-accent/30" />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="04XX XXX XXX" {...field} className="bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-accent focus-visible:ring-accent/50 transition-all duration-300 hover:border-accent/30" />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Suburb</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Gorokan" {...field} className="bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-accent focus-visible:ring-accent/50 transition-all duration-300 hover:border-accent/30" />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Project details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your painting needs (Interior, Exterior, Residential or Industrial)..." {...field} className="min-h-[120px] bg-background border-input text-foreground placeholder:text-muted-foreground focus-visible:border-accent focus-visible:ring-accent/50 transition-all duration-300 hover:border-accent/30 resize-none" />
            </FormControl>
            <FormMessage className="text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm text-center">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-accent hover:text-accent-foreground hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 shadow-lg shadow-primary/10 tracking-wide uppercase">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : "Request Free Quote"}
      </Button>
    </Form>;
}
export default function FaleConosco() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: MapPin,
    label: "Service Areas",
    value: "Central Coast, NSW"
  }, {
    icon: Phone,
    label: "Call Clint",
    value: "0413 771 815"
  }, {
    icon: Mail,
    label: "Email Us",
    value: "Folppclint430@gmail.com"
  }, {
    icon: Clock,
    label: "Business Hours",
    value: "Mon - Sat: 7:00 AM - 5:00 PM"
  }];
  return <section id="get-a-quote" className="relative w-full py-20 lg:py-32 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588340/img/deep_forest_background.png)] bg-cover bg-center bg-no-repeat opacity-5 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {}
          <div ref={textRef} className={cn("lg:col-span-5 flex flex-col justify-center space-y-10 transition-all duration-1000 ease-out", textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 backdrop-blur-sm">
                <span className="size-2 rounded-full bg-accent animate-pulse" />
                <span className="text-xs font-semibold text-accent uppercase tracking-widest">
                  Get a Quote
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold text-primary leading-tight tracking-tight">
                Quality Painting, Built on 30 Years Experience
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
                Ready to transform your home or business? We provide premium painting solutions 
                across the Northern Central Coast. Contact Clint today for a professional, 
                no-obligation estimate.
              </p>
            </div>

            <div className="space-y-6 pt-4">
              {contactDetails.map((item, i) => <div key={i} data-index={i} className="group flex items-start gap-4 p-4 rounded-lg hover:bg-accent/5 transition-colors duration-300 border border-transparent hover:border-accent/10">
                  <div className="shrink-0 p-3 rounded-md bg-muted text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors duration-300">
                    <item.icon className="size-5 stroke-[1.5px]" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      {item.label}
                    </p>
                    <p className="text-base text-foreground font-medium">
                      {item.value || "Information not available"}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div ref={formRef} className={cn("lg:col-span-7 transition-all duration-1000 ease-out delay-200", formVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10")}>
            <div className="relative rounded-2xl overflow-hidden bg-card border border-border shadow-2xl shadow-primary/5">
              {}
              <div className="absolute -top-24 -right-24 size-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 size-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

              <div className="relative p-6 md:p-10">
                <div className="mb-8">
                  <h3 className="text-2xl font-sans font-bold text-primary mb-2">
                    Get Your Free Quote
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Tell us about your project and we&apos;ll get back to you with a professional estimate within 24 hours.
                  </p>
                </div>

                <FormProvider formKey="quote_request_contact" formId="quote_request_contact" sectionName="get-a-quote" defaultValues={{
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Request Free Quote" successMessage="Thank you! Your quote request has been sent. Clint will be in touch shortly.">
                  <ContactForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}